import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowDown,
  Menu,
  X,
  Gamepad2,
  Search,
  BookOpenCheck,
  Sparkles,
  Github,
} from 'lucide-react';

interface Project {
  id: string;
  title: string;
  path: string;
  to: string;
  icon: React.ElementType;
  gradient: string;
  description: string;
  tags: string[];
}

const projects: Project[] = [
  {
    id: 'css-animation',
    title: 'Доклад по JS',
    path: '/css-animation',
    to: '/css-animation',
    icon: BookOpenCheck,
    gradient: 'from-purple-500 to-pink-500',
    description:
      'Презентация, доклад, кроссворд и интерактивный CSS-плейграунд — всё про анимации.',
    tags: ['Презентация', 'Кроссворд', 'Playground'],
  },
  {
    id: 'hunger-games',
    title: 'Hunger Games',
    path: '/hunger-games',
    to: '/hunger-games',
    icon: Gamepad2,
    gradient: 'from-blue-500 to-cyan-500',
    description:
      'Голодные игры — лор, правила и информация о турнире.',
    tags: ['Турнир', 'Minecraft', 'Правила'],
  },
  {
    id: 'lost-ones',
    title: 'Lost Ones',
    path: '/lost-ones',
    to: '/lost-ones',
    icon: Search,
    gradient: 'from-emerald-500 to-teal-500',
    description: 'Стена пропавших — постеры, фото, музыка и интерактив.',
    tags: ['Интерактив', 'Музыка', 'Постеры'],
  },
];

const navLinks = [
  { label: 'Проекты', to: '#projects' },
  { label: 'О нас', to: '#about' },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAnchor = (e: React.MouseEvent, hash: string) => {
    e.preventDefault();
    setMenuOpen(false);
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      {/* фон */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950 via-black to-blue-950 opacity-70 pointer-events-none" />
      <motion.div
        animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
        transition={{ duration: 30, repeat: Infinity, repeatType: 'reverse' }}
        className="fixed inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(168,85,247,0.18),transparent_55%),radial-gradient(circle_at_70%_80%,rgba(34,211,238,0.18),transparent_55%)] pointer-events-none"
      />

      {/* navbar */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-black/70 backdrop-blur-xl border-b border-white/10'
            : 'bg-transparent'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-cyan-500 flex items-center justify-center text-sm font-black">
              СП
            </span>
            <span className="text-lg font-black tracking-tight">
              Студ.&nbsp;проекты
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <a
                key={l.to}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.to)}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#projects"
              onClick={(e) => handleAnchor(e, '#projects')}
              className="ml-2 px-4 py-2 text-sm font-semibold bg-white text-black rounded-lg hover:bg-gray-200 transition"
            >
              Открыть
            </a>
          </div>

          <button
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Меню"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
            >
              <div className="px-4 py-4 flex flex-col gap-2">
                {navLinks.map((l) => (
                  <a
                    key={l.to}
                    href={l.to}
                    onClick={(e) => handleAnchor(e, l.to)}
                    className="px-4 py-3 text-base font-medium text-gray-200 hover:bg-white/5 rounded-lg transition"
                  >
                    {l.label}
                  </a>
                ))}
                <div className="h-px bg-white/10 my-2" />
                <p className="px-4 text-xs uppercase tracking-wider text-gray-500">
                  Проекты
                </p>
                {projects.map((p) => (
                  <Link
                    key={p.id}
                    to={p.to}
                    onClick={() => setMenuOpen(false)}
                    className="px-4 py-3 text-base text-white hover:bg-white/5 rounded-lg flex items-center gap-3 transition"
                  >
                    <span
                      className={`w-8 h-8 rounded-lg bg-gradient-to-br ${p.gradient} flex items-center justify-center flex-shrink-0`}
                    >
                      <p.icon className="w-4 h-4 text-white" />
                    </span>
                    {p.title}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* hero */}
      <section className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 pt-24 pb-16 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8"
        >
          <Sparkles className="w-4 h-4 text-yellow-300" />
          <span className="text-xs sm:text-sm text-gray-300">
            Сборник студенческих работ
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 leading-[0.95]"
        >
          Студенческие
          <br />
          проекты
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 text-lg sm:text-xl md:text-2xl text-gray-300 max-w-2xl"
        >
          Доклады, игры и эксперименты в одном месте. Выбирай куда дальше.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-3"
        >
          <a
            href="#projects"
            onClick={(e) => handleAnchor(e, '#projects')}
            className="w-full sm:w-auto px-8 py-3.5 bg-white text-black rounded-xl font-semibold hover:bg-gray-200 transition flex items-center justify-center gap-2"
          >
            Смотреть проекты
            <ArrowDown className="w-4 h-4" />
          </a>
          <a
            href="#about"
            onClick={(e) => handleAnchor(e, '#about')}
            className="w-full sm:w-auto px-8 py-3.5 bg-white/5 border border-white/15 text-white rounded-xl font-semibold hover:bg-white/10 transition"
          >
            О площадке
          </a>
        </motion.div>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2.5, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-gray-500"
        >
          <span className="text-xs uppercase tracking-widest">scroll</span>
          <ArrowDown className="w-4 h-4" />
        </motion.div>
      </section>

      {/* projects */}
      <section
        id="projects"
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight">
            Проекты
          </h2>
          <p className="mt-4 text-base sm:text-lg text-gray-400">
            Три самостоятельных приложения — открывай любое.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {projects.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={p.to}
                  className="group relative block h-full rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden hover:border-white/30 transition-all duration-300 hover:-translate-y-1"
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${p.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                  />

                  <div className="relative p-6 sm:p-7 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-5">
                      <div
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${p.gradient} flex items-center justify-center shadow-lg`}
                      >
                        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </div>

                    <h3 className="text-xl sm:text-2xl font-bold mb-2">
                      {p.title}
                    </h3>
                    <p className="text-xs text-gray-500 font-mono mb-3 truncate">
                      {p.path}
                    </p>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-5 flex-1">
                      {p.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="text-xs px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-gray-300"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* about */}
      <section
        id="about"
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 p-8 sm:p-12 text-center"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-4">
            Что это вообще такое?
          </h2>
          <p className="text-base sm:text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
            Сборник наших учебных и не очень проектов под одной крышей. Раньше
            каждое приложение жило отдельно — теперь у них общее меню и
            навигация.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3 text-sm text-gray-500">
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              React 19
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              TypeScript
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              Tailwind
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              Framer Motion
            </span>
          </div>
        </motion.div>
      </section>

      {/* footer */}
      <footer className="relative z-10 border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Студенческие проекты</p>
          <a
            href="https://github.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-white transition"
          >
            <Github className="w-4 h-4" />
            <span>GitHub</span>
          </a>
        </div>
      </footer>
    </div>
  );
}
